import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useAppSelector } from '../../store/hooks';
import { FaRobot, FaSync, FaExclamationTriangle, FaCheckCircle, FaThLarge, FaList, FaChartLine } from 'react-icons/fa';
import './AdminComponents.css';

interface ActiveTradingBotsProps {
  className?: string;
}

interface TradingBot {
  id: number;
  name: string;
  status: string;
  strategyType: string;
  paperTrading: boolean;
  lastError?: string | null;
}

type LayoutType = 'list' | 'cards';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper functions for cache management
const getCachedData = (key: string) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < CACHE_DURATION) {
        console.log(`ActiveTradingBots: Using cached data for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        return data;
      } else {
        console.log(`ActiveTradingBots: Cache expired for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error(`Failed to get cached data for ${key}:`, error);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    console.log(`ActiveTradingBots: Cached data for ${key}`);
  } catch (error) {
    console.error(`Failed to cache data for ${key}:`, error);
  }
};

const ActiveTradingBots: React.FC<ActiveTradingBotsProps> = ({ className = '' }) => {
  const adminApi = useAdminApiService();
  const navigate = useNavigate();
  const adminUser = useAppSelector((state: any) => state?.auth?.adminUser);
  const cacheKey = `active-trading-bots:${adminUser?.id || adminUser?.username || 'unknown'}`;
  const [bots, setBots] = useState<TradingBot[]>(() => {
    const cached = getCachedData(cacheKey);
    return cached?.bots || [];
  });
  const [count, setCount] = useState(() => {
    const cached = getCachedData(cacheKey);
    return cached?.count || 0;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [layout, setLayout] = useState<LayoutType>(() => {
    const savedLayout = localStorage.getItem('activeTradingBotsLayout');
    return (savedLayout as LayoutType) || 'cards';
  });

  // Helper function to get status badge configuration
  const getStatusBadge = (bot: TradingBot) => {
    // Check for error states first
    if (bot.lastError) {
      if (bot.lastError.includes('INSUFFICIENT')) {
        return {
          text: '⚠️ INSUFFICIENT BALANCE',
          bgColor: '#fee',
          textColor: '#dc3545',
          borderColor: '#dc3545'
        };
      }
      if (bot.lastError.includes('VOLATILITY')) {
        return {
          text: '🛡️ VOLATILITY BLOCKED',
          bgColor: '#fff3cd',
          textColor: '#856404',
          borderColor: '#856404'
        };
      }
    }

    // Check bot status
    if (bot.status === 'ACTIVE') {
      if (bot.paperTrading) {
        return {
          text: '📝 PAPER TRADING',
          bgColor: '#ffc107',
          textColor: '#000',
          borderColor: '#ffc107'
        };
      }
      return {
        text: '✅ TRADING',
        bgColor: '#d4f4dd',
        textColor: '#10b981',
        borderColor: '#10b981'
      };
    }

    if (bot.status === 'PAUSED') {
      return {
        text: '⏸️ PAUSED',
        bgColor: '#fef3c7',
        textColor: '#f59e0b',
        borderColor: '#f59e0b'
      };
    }

    if (bot.status === 'STOPPED') {
      return {
        text: '⏹️ STOPPED',
        bgColor: '#fee',
        textColor: '#dc2626',
        borderColor: '#dc2626'
      };
    }

    // Default fallback
    return {
      text: bot.status,
      bgColor: '#f3f4f6',
      textColor: '#6b7280',
      borderColor: '#6b7280'
    };
  };

  const loadBots = async (isRefresh = false) => {
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        console.log('ActiveTradingBots: Using cached data, skipping API call');
        setBots(cached.bots || []);
        setCount(cached.count || 0);
        setLastUpdated(cached.lastUpdated ? new Date(cached.lastUpdated) : null);
        setLoading(false);
        return;
      }
    }

    console.log('ActiveTradingBots: Making API call', { isRefresh });
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getActiveTradingBots();
      if (response.success && response.data) {
        const botsData = response.data.bots || [];
        const countData = response.data.count || 0;
        const updatedTime = new Date();

        setBots(botsData);
        setCount(countData);
        setLastUpdated(updatedTime);
        
        // Cache the response
        setCachedData(cacheKey, {
          bots: botsData,
          count: countData,
          lastUpdated: updatedTime.toISOString()
        });
      } else {
        setError(response.error || 'Failed to load active trading bots');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load active trading bots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBots(false); // Check cache on mount
    // Auto-refresh every 2 minutes (force refresh)
    const interval = setInterval(() => loadBots(true), 120000);
    return () => clearInterval(interval);
  }, [cacheKey]);

  const handleLayoutChange = (newLayout: LayoutType) => {
    setLayout(newLayout);
    localStorage.setItem('activeTradingBotsLayout', newLayout);
  };

  // Navigate to Trading Bots page and open analytics modal for specific bot
  const openAnalytics = (botId: number) => {
    navigate('/admin/trading-bots', { 
      state: { 
        openAnalyticsForBot: botId 
      } 
    });
  };

  // Render Cards Layout
  const renderCardsLayout = () => (
    <div className="bots-cards-layout">
      {bots.map((bot) => {
        const statusBadge = getStatusBadge(bot);
        return (
          <div
            key={bot.id}
            className={`bot-card ${bot.paperTrading ? 'paper-trading-card' : 'live-trading-card'}`}
          >
            <div className="bot-card-header">
              <div className="bot-card-icon">
                <FaRobot style={{ fontSize: '32px', color: bot.paperTrading ? '#fbb445' : '#0dca9d' }} />
              </div>
              <div className="bot-card-status-badge" style={{
                backgroundColor: bot.paperTrading ? '#fbb445' : '#0dca9d',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                {bot.paperTrading ? 'Paper' : 'Live'}
              </div>
            </div>
            <div className="bot-card-content">
              <div className="bot-card-name">{bot.name}</div>
              <div className="bot-card-type">{bot.strategyType || 'Unknown Type'}</div>
            </div>
            <div className="bot-card-footer" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%'
            }}>
              <div className="bot-card-status" style={{
                backgroundColor: statusBadge.bgColor,
                color: statusBadge.textColor,
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                textAlign: 'center',
                border: `1px solid ${statusBadge.borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '36px',
                boxSizing: 'border-box',
                width: '100%'
              }}>
                {statusBadge.text}
              </div>
              <button
                onClick={() => openAnalytics(bot.id)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: '1px solid #667eea',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  height: '36px',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5568d3';
                  e.currentTarget.style.borderColor = '#5568d3';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <FaChartLine style={{ fontSize: '11px' }} />
                <span>Analytics</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render List Layout
  const renderListLayout = () => (
    <div className="bots-list" style={{ padding: '16px' }}>
      {bots.map((bot) => {
        const statusBadge = getStatusBadge(bot);
        return (
          <div
            key={bot.id}
            className="bot-item"
            style={{
              padding: '12px',
              marginBottom: '8px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '4px' 
              }}>
                <FaCheckCircle style={{ 
                  color: '#0dca9d', 
                  fontSize: '14px', 
                  marginRight: '8px' 
                }} />
                <span style={{ 
                  fontWeight: '600', 
                  color: '#1f2937',
                  fontSize: '14px'
                }}>
                  {bot.name}
                </span>
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                marginLeft: '22px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{bot.strategyType || 'Unknown Type'}</span>
                <span style={{
                  padding: '2px 6px',
                  backgroundColor: bot.paperTrading ? '#fbb445' : '#0dca9d',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {bot.paperTrading ? 'Paper' : 'Live'}
                </span>
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                padding: '6px 12px',
                backgroundColor: statusBadge.bgColor,
                color: statusBadge.textColor,
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                border: `1px solid ${statusBadge.borderColor}`
              }}>
                {statusBadge.text}
              </div>
              <button
                onClick={() => openAnalytics(bot.id)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: '1px solid #667eea',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s',
                  height: 'fit-content'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5568d3';
                  e.currentTarget.style.borderColor = '#5568d3';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                  e.currentTarget.style.borderColor = '#667eea';
                }}
              >
                <FaChartLine style={{ fontSize: '11px' }} />
                <span>Analytics</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={`active-trading-bots-card ${className}`}>
      <div className="card-header">
        <div className="card-title">
          <FaRobot style={{ color: '#086afb', fontSize: '20px', marginRight: '8px' }} />
          <h3>Trading Strategy Bots</h3>
        </div>
        <div className="card-actions">
          <div className="layout-switcher">
            <button 
              className={`layout-btn ${layout === 'cards' ? 'active' : ''}`} 
              onClick={() => handleLayoutChange('cards')}
              title="Cards Layout"
            >
              <FaThLarge />
            </button>
            <button 
              className={`layout-btn ${layout === 'list' ? 'active' : ''}`} 
              onClick={() => handleLayoutChange('list')}
              title="List Layout"
            >
              <FaList />
            </button>
          </div>
          <button
            className="refresh-btn"
            onClick={() => loadBots(true)}
            disabled={loading}
            title="Refresh bots list"
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ margin: '16px', padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33' }}>
          <FaExclamationTriangle style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      {loading && !error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <FaSync className="spinning" style={{ fontSize: '24px', marginBottom: '12px' }} />
          <div>Loading active bots...</div>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="bots-summary" style={{ 
            padding: '16px', 
            backgroundColor: '#f9fafb', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                {count}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {count === 1 ? 'Trading Bot' : 'Trading Bots'}
              </div>
            </div>
            {lastUpdated && (
              <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right' }}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>

          {count === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <FaRobot style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.3 }} />
              <div>No trading bots</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                No trading strategy bots have been created yet
              </div>
            </div>
          ) : (
            <>
              {layout === 'cards' && renderCardsLayout()}
              {layout === 'list' && renderListLayout()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ActiveTradingBots;

